import FacturacionPedidoService from "@/services/FacturacionPedidoService";

/**
 * Vuex module for the facturación on-demand flow (Trello #223).
 *
 * Shape follows the gold reference: store/modules/customers.js
 * - Namespaced
 * - state() factory (avoids shared reference across tests)
 * - SCREAMING_SNAKE synchronous mutations
 * - async/await actions with SET_LOADING/SET_FACTURANDO in finally
 * - Errors are re-thrown to the caller (component/global toast handles UI)
 */

const state = () => ({
  pedido: null,
  loading: false,
  facturando: false,
  error: null,

  // Cliente search (for Consumidor Final pedidos)
  clientes: [],
  clientesLoading: false,
  clienteSeleccionado: null,
});

const getters = {};

const actions = {
  /**
   * Fetches a pedido by ID.
   * Commits SET_LOADING true before, false in finally.
   */
  async fetchPedido({ commit }, id) {
    try {
      commit("SET_LOADING", true);
      const response = await FacturacionPedidoService.buscarPedido(id);
      commit("SET_PEDIDO", response.data.data);
      commit("SET_ERROR", null);
      return response.data;
    } catch (error) {
      commit("SET_ERROR", error.message || "Error al cargar el pedido");
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  /**
   * Searches clientes for the autocomplete (GET /clientes/resumido).
   * Toggling SET_CLIENTES_LOADING in try/finally.
   */
  async searchClientes({ commit }, query) {
    try {
      commit("SET_CLIENTES_LOADING", true);
      const response = await FacturacionPedidoService.buscarClientes(query);
      commit("SET_CLIENTES", response.data.data);
      return response.data;
    } catch (error) {
      commit("SET_ERROR", error.message || "Error al buscar clientes");
      throw error;
    } finally {
      commit("SET_CLIENTES_LOADING", false);
    }
  },

  /**
   * Fetches the full cliente record for the selected cliente.
   * Used to populate fiscal fields after autocomplete selection.
   */
  async fetchClienteSeleccionado({ commit }, id) {
    try {
      const response = await FacturacionPedidoService.getCliente(id);
      // GET /clientes/:id devuelve el cliente directo (res.json(cliente)), no anidado en { data }
      commit("SET_CLIENTE_SELECCIONADO", response.data);
      commit("SET_ERROR", null);
      return response.data;
    } catch (error) {
      commit("SET_ERROR", error.message || "Error al cargar el cliente");
      throw error;
    }
  },

  /**
   * Emits an ARCA invoice for the given pedido.
   * Commits SET_FACTURANDO true before, false in finally.
   * Re-throws on error so the component can display the backend message.
   */
  async facturarPedido({ commit }, { id, payload }) {
    try {
      commit("SET_FACTURANDO", true);
      const response = await FacturacionPedidoService.facturar(id, payload);
      commit("SET_ERROR", null);
      return response.data;
    } catch (error) {
      commit("SET_ERROR", error.message || "Error al facturar el pedido");
      throw error;
    } finally {
      commit("SET_FACTURANDO", false);
    }
  },
};

const mutations = {
  SET_PEDIDO(state, pedido) {
    state.pedido = pedido;
  },
  SET_LOADING(state, value) {
    state.loading = value;
  },
  SET_FACTURANDO(state, value) {
    state.facturando = value;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  SET_CLIENTES(state, clientes) {
    state.clientes = clientes;
  },
  SET_CLIENTES_LOADING(state, value) {
    state.clientesLoading = value;
  },
  SET_CLIENTE_SELECCIONADO(state, cliente) {
    state.clienteSeleccionado = cliente;
  },
  RESET(state) {
    state.pedido = null;
    state.loading = false;
    state.facturando = false;
    state.error = null;
    state.clientes = [];
    state.clientesLoading = false;
    state.clienteSeleccionado = null;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
